import { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Copy, ErrorFilled } from '@carbon/react/icons';
import ErrorToast from 'components/error-toast';
import { SuccessToast } from 'components/success-toast';
import {
  EventsPanel,
  EventsPanelOpenButton,
} from 'components/events-panel/events-panel';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { PageHeader } from 'components/page-header';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import type { AreaSelection, DevelopmentPaceScenarioFormData } from './types';
import { ScenarioFormProvider, useScenarioForm } from './contexts';
import {
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  areaGeometriesAtom,
  cloneScenarioLoadableAtom,
  toastAtom,
} from './atoms';
import { AreaConfigs } from './area-configs';
import { AreaRelatedDocuments } from './area-related-documents';
import { CaptureSection } from './capture/capture-section';
import { CapturePreviewProvider } from './capture-preview-context';
import { SchedulePreviewSection } from './schedule-preview';
import { GlobalConfigs } from './global-configs';
import { ScenarioNav } from './scenario-nav';
import { ScenarioMap } from './scenario-map';
import { getMemoizedMapAreas } from './map-areas';
import { WellLifecycleContent } from './well-lifecycle-content';
import {
  GLOBAL_SELECTION_ID,
  buildNavItems,
  buildSaveBlockedReasons,
  deriveScenarioStatuses,
  findInvalidAreaIds,
  getAreaDisplayName,
  type FormMode,
} from './utils';
import styles from './scenario-form.module.scss';

/** Ties the disabled Save button to its explanation via aria-describedby. */
const SAVE_BLOCKED_ID = 'dps-save-blocked';

interface ScenarioFormProps {
  initialValues: DevelopmentPaceScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}

/**
 * Main form component for creating/editing development pace scenarios.
 * Wraps content in ScenarioFormProvider and renders the full layout.
 * @param {ScenarioFormProps} props - The form props
 * @return {JSX.Element} The scenario form component
 */
export function ScenarioForm({
  initialValues,
  mode,
  scenarioId,
  commentsUrl,
  eventsUrl,
}: ScenarioFormProps): JSX.Element {
  const geometries = useAtomValue(areaGeometriesAtom);

  return (
    <ScenarioFormProvider
      initialValues={initialValues}
      mode={mode}
      scenarioId={scenarioId}
      geometries={geometries}
    >
      <ScenarioFormContent
        scenarioId={scenarioId}
        commentsUrl={commentsUrl}
        eventsUrl={eventsUrl}
      />
    </ScenarioFormProvider>
  );
}

/**
 * Inner content component that consumes the form context.
 * Uses form.Subscribe for reactive UI to avoid full re-renders.
 * @return {JSX.Element} The form content layout
 */
function ScenarioFormContent({
  scenarioId,
  commentsUrl,
  eventsUrl,
}: {
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}): JSX.Element {
  const {
    form,
    mode,
    selectedId,
    setSelectedId,
    addArea,
    removeArea,
    handleSave,
    isSaving,
  } = useScenarioForm();

  const [configExpanded, setConfigExpanded] = useState(true);
  const [lifecycleExpanded, setLifecycleExpanded] = useState(true);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);

  const geometries = useAtomValue(areaGeometriesAtom);
  const [cloneState, triggerClone] = useAtom(cloneScenarioLoadableAtom);
  const [toast, setToast] = useAtom(toastAtom);
  const history = useHistory();

  // Trigger lookup fetches on mount — children subscribe to the same atoms
  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);

  useEffect(() => {
    if (categoriesState.state === 'hasError') {
      setToast({ message: 'Failed to load area categories', type: 'error' });
    }
  }, [categoriesState.state, setToast]);

  useEffect(() => {
    if (unitTypesState.state === 'hasError') {
      setToast({ message: 'Failed to load unit types', type: 'error' });
    }
  }, [unitTypesState.state, setToast]);

  // Re-trigger form validation when geometries change (async fetch completed)
  useEffect(() => {
    if (Object.keys(geometries).length > 0) {
      form.validate('change');
    }
  }, [geometries, form]);

  const isCloning = cloneState.state === 'loading';

  const handleClone = () => {
    if (!scenarioId) return;
    triggerClone(Number(scenarioId))
      .then((data) => {
        history.push(data.href);
      })
      .catch(() => {});
  };

  const scenarioName = form.getFieldValue('global.configurationName');

  const title =
    mode === 'create'
      ? 'Add Development Pace Scenario'
      : scenarioName || 'Edit Development Pace Scenario';

  return (
    <div className={styles.scenarioForm}>
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
      <PageHeader
        title={title}
        breadcrumbs={[]}
        actions={
          mode === 'edit' ? (
            <div className={styles.headerActions}>
              <EventsPanelOpenButton />
              <Button
                kind="tertiary"
                size="md"
                renderIcon={Copy}
                disabled={isCloning}
                onClick={handleClone}
              >
                {isCloning ? <InlineLoading /> : 'Clone Scenario'}
              </Button>
            </div>
          ) : null
        }
      />
      <form.Subscribe
        selector={(state) =>
          getMemoizedMapAreas(state.values.areas, geometries)
        }
      >
        {(mapAreas) => (
          <ScenarioMap
            areas={mapAreas}
            focusedId={typeof selectedId === 'number' ? selectedId : null}
          />
        )}
      </form.Subscribe>
      <div className={styles.mainPanel}>
        <form.Subscribe
          selector={(state) =>
            buildNavItems(
              state.values.areas,
              getMemoizedOverlappingAreaIds(state.values.areas, geometries),
              findInvalidAreaIds(state.values)
            )
          }
        >
          {(navItems) => (
            <ScenarioNav
              items={navItems}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onAdd={addArea}
              onDelete={removeArea}
            />
          )}
        </form.Subscribe>

        <div className={styles.contentPanel}>
          <form.Subscribe
            selector={(state) => {
              const { areas } = state.values;
              const { overlappingAreaIds, ...statuses } =
                deriveScenarioStatuses(state.values, geometries);
              const area =
                selectedId !== GLOBAL_SELECTION_ID
                  ? areas.find((a: AreaSelection) => a.id === selectedId)
                  : undefined;
              const isGeometryLoading = areas.some(
                (a: AreaSelection) =>
                  a.configurableId !== null && !geometries[a.id]
              );
              // Derived from state.values, never from canSubmit or the form
              // errorMap: canSubmit is `submissionAttempts === 0 && !isTouched
              // && !hasOnMountError || ...`, so on a freshly-loaded edit form
              // holding an already-invalid value it reads true while the
              // errorMap is populated. Gating the banner on either would show
              // a red explanation beside a working button.
              const saveBlocked = buildSaveBlockedReasons({
                values: state.values,
                invalidAreaIds: findInvalidAreaIds(state.values),
                overlappingAreaIds,
                isGeometryLoading,
              });
              return {
                ...statuses,
                canSubmit: state.canSubmit,
                isDirty: state.isDirty,
                contentTitle: area ? getAreaDisplayName(area) : 'Global',
                isGeometryLoading,
                saveBlockedReasons: saveBlocked.reasons,
                saveBlockedIsHard: saveBlocked.hasHardBlock,
                selectedAreaOverlap:
                  area && overlappingAreaIds.has(area.id)
                    ? 'Overlaps with an existing area'
                    : undefined,
              };
            }}
          >
            {({
              canSubmit,
              isDirty,
              contentTitle,
              hasOverlap,
              isGeometryLoading,
              hasName,
              hasTcScenario,
              hasCompleteArea,
              noAreas,
              saveBlockedReasons,
              saveBlockedIsHard,
              selectedAreaOverlap,
            }) => {
              // Save is gated on the full mock rule: a name, a Type Curve
              // Scenario, at least one geometry-complete area, and no inline or
              // overlap errors.
              //
              // !saveBlockedIsHard is defence in depth, not a live fix. It
              // exists because canSubmit is
              //   submissionAttempts === 0 && !isTouched && !hasOnMountError
              //     || !isValidating && !isSubmitting && isValid
              //     || submitInvalid
              // so an UNTOUCHED form holding an invalid value reads true. That
              // state is not reachable from page.tsx today: setFieldValue sets
              // isTouched and isDirty together, so isDirty implies isTouched
              // and the isDirty term below already excludes it in edit mode;
              // and create mode starts with zero areas, so hasCompleteArea
              // blocks until addArea touches the form. The term is kept anyway
              // so the button and the banner cannot disagree if an entry point
              // ever seeds different values — this is exactly the class of
              // wrong assumption that produced ONCALL-840, the previous comment
              // here having asserted that canSubmit "blocks any incomplete
              // area" when it does not.
              //
              // hasName, hasTcScenario and !hasOverlap are now exactly
              // redundant with saveBlockedIsHard. hasCompleteArea, canSubmit
              // and !isGeometryLoading are NOT: hasCompleteArea uniquely
              // blocks the zero-area case, which buildSaveBlockedReasons
              // deliberately leaves to the standing "No areas" notification.
              const isSaveEnabled =
                hasName &&
                hasTcScenario &&
                hasCompleteArea &&
                canSubmit &&
                !saveBlockedIsHard &&
                (mode === 'create' || isDirty) &&
                !hasOverlap &&
                !isGeometryLoading;

              return (
                <>
                  <div className={styles.contentHeader}>
                    <div className={styles.contentTitleRow}>
                      <h4 className={styles.contentTitle}>{contentTitle}</h4>
                      {/* Kept alongside the banner's "Overlapping geometries"
                          line: this one sits against the panel title, so it
                          identifies the overlap without the user having to map
                          a name back to a nav row. */}
                      {selectedAreaOverlap && (
                        <span className={styles.overlapError}>
                          <ErrorFilled className={styles.overlapErrorIcon} />
                          {selectedAreaOverlap}
                        </span>
                      )}
                    </div>
                    <div className={styles.contentActions}>
                      {isDirty && (
                        <InlineNotification
                          kind="info"
                          title="Unsaved changes"
                          lowContrast
                          hideCloseButton
                        />
                      )}
                      <Button
                        kind="primary"
                        size="md"
                        disabled={!isSaveEnabled || isSaving}
                        onClick={handleSave}
                        aria-describedby={
                          saveBlockedReasons.length > 0
                            ? SAVE_BLOCKED_ID
                            : undefined
                        }
                      >
                        {isSaving ? 'Saving...' : 'Save Scenario'}
                      </Button>
                    </div>
                  </div>
                  {saveBlockedReasons.length > 0 && (
                    // Plain-text subtitle only: InlineNotification runs
                    // useNoInteractiveChildren, which THROWS (not warns) on any
                    // focusable child outside production, so a "jump to area"
                    // link here would crash dev and the test run. The nav's
                    // per-area error icon is the navigation affordance instead.
                    // role is left at Carbon's "status" default - the reasons
                    // change on every keystroke while the user corrects a
                    // value, and "alert" would interrupt a screen reader
                    // continuously.
                    <InlineNotification
                      id={SAVE_BLOCKED_ID}
                      className={styles.saveBlocked}
                      kind={saveBlockedIsHard ? 'warning' : 'info'}
                      lowContrast
                      hideCloseButton
                      title="Save unavailable"
                      subtitle={saveBlockedReasons.join(' ')}
                    />
                  )}
                  {noAreas && (
                    <InlineNotification
                      kind="warning"
                      lowContrast
                      hideCloseButton
                      title="No areas"
                      subtitle="This scenario has no areas and schedules nothing — add an area to begin."
                    />
                  )}
                </>
              );
            }}
          </form.Subscribe>

          {selectedId === GLOBAL_SELECTION_ID ? (
            <Accordion
              expanded={configExpanded}
              onClick={() => setConfigExpanded((prev) => !prev)}
            >
              <AccordionSummary>
                <h4 className={styles.accordionTitle}>Global Configs</h4>
              </AccordionSummary>
              <AccordionDetails>
                <GlobalConfigs />
              </AccordionDetails>
            </Accordion>
          ) : (
            <form.Subscribe
              selector={(state) =>
                state.values.areas.findIndex(
                  (a: AreaSelection) => a.id === selectedId
                )
              }
            >
              {(areaIndex) => {
                return areaIndex >= 0 ? (
                  <Accordion
                    expanded={configExpanded}
                    onClick={() => setConfigExpanded((prev) => !prev)}
                  >
                    <AccordionSummary>
                      <h4 className={styles.accordionTitle}>Area Configs</h4>
                    </AccordionSummary>
                    <AccordionDetails>
                      <AreaConfigs areaIndex={areaIndex} />
                    </AccordionDetails>
                  </Accordion>
                ) : null;
              }}
            </form.Subscribe>
          )}

          <form.Subscribe
            selector={(state) => ({
              referenceDateMode: state.values.global.referenceDateMode,
              referenceDate: state.values.global.referenceDate,
              areaIndex:
                selectedId !== GLOBAL_SELECTION_ID
                  ? state.values.areas.findIndex(
                      (a: AreaSelection) => a.id === selectedId
                    )
                  : -1,
            })}
          >
            {({ referenceDateMode, referenceDate, areaIndex }) => {
              const referenceMoment =
                referenceDateMode === 'fixed' && referenceDate
                  ? moment(referenceDate)
                  : moment();

              // Pace parameters are per-area now — the global selection has no
              // Well Lifecycle section (the Global Default tier was removed).
              if (selectedId === GLOBAL_SELECTION_ID) return null;

              const fieldPrefix = `areas[${areaIndex}]`;

              return (
                <Accordion
                  expanded={lifecycleExpanded}
                  onClick={() => setLifecycleExpanded((prev) => !prev)}
                >
                  <AccordionSummary>
                    <h4 className={styles.accordionTitle}>
                      Well Lifecycle Parameters
                    </h4>
                  </AccordionSummary>
                  <AccordionDetails>
                    <WellLifecycleContent
                      fieldPrefix={fieldPrefix}
                      referenceDate={referenceMoment}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            }}
          </form.Subscribe>

          {/* Related Documents per area (10-SFD): the shipped Related Items widget on
              a saved area; a save-first hint until the area is persisted. */}
          {selectedId !== GLOBAL_SELECTION_ID && (
            <form.Subscribe
              selector={(state) =>
                state.values.areas.findIndex(
                  (a: AreaSelection) => a.id === selectedId
                )
              }
            >
              {(areaIndex) => {
                if (areaIndex < 0) return null;
                return (
                  <Accordion
                    expanded={documentsExpanded}
                    onClick={() => setDocumentsExpanded((prev) => !prev)}
                  >
                    <AccordionSummary>
                      <h4 className={styles.accordionTitle}>
                        Related Documents
                      </h4>
                    </AccordionSummary>
                    <AccordionDetails>
                      <AreaRelatedDocuments
                        areaIndex={areaIndex}
                        scenarioId={scenarioId}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              }}
            </form.Subscribe>
          )}

          {/* Live capture feedback + editable-ephemeral schedule preview, both reading
              one shared capture result (03-CAP §5 chips + 08-PRV §5.1 chart), recomputed
              (debounced) as the definition changes. */}
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const areaIndex =
                selectedId === GLOBAL_SELECTION_ID
                  ? -1
                  : values.areas.findIndex(
                      (a: AreaSelection) => a.id === selectedId
                    );
              return (
                <CapturePreviewProvider values={values}>
                  <CaptureSection values={values} areaIndex={areaIndex} />
                  <SchedulePreviewSection areaIndex={areaIndex} />
                </CapturePreviewProvider>
              );
            }}
          </form.Subscribe>
        </div>
      </div>
      {mode === 'edit' && commentsUrl && eventsUrl && (
        <EventsPanel
          header={scenarioName || ''}
          title="Development Pace Scenario"
          helperText="Share information about this scenario."
          commentsHref={commentsUrl}
          eventsHref={eventsUrl}
        />
      )}
    </div>
  );
}
